import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AGAVE_VIEWBOX, AGAVE_TRANSFORM, AGAVE_PATHS } from "./agavePath";

const EASE = [0.16, 1, 0.3, 1];
const DRAW = 5.2;      // seconds to draw the outline
const FILL_DELAY = 4.4; // colour begins filling before the outline finishes
const TOTAL = 8000;    // total loader lifetime (ms)

export const Preloader = () => {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname === "/";
  });

  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      setShow(false);
    }, TOTAL);
    return () => { clearTimeout(t); document.body.style.overflow = prev; };
  }, [show]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="preloader"
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: "linear-gradient(180deg,#6a7bee 0%,#7d63cf 54%,#7a52b0 100%)" }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, transition: { duration: 0.9, ease: [0.7, 0, 0.3, 1] } }}
          data-testid="preloader"
        >
          {/* soft radiating glow behind the mark */}
          <motion.div
            className="absolute rounded-full"
            style={{ width: 620, height: 620, background: "radial-gradient(circle, rgba(255,255,255,0.26), transparent 62%)" }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0, 0.75, 0.55], scale: [0.6, 1.1, 1] }}
            transition={{ duration: 3, ease: "easeOut" }}
          />

          <div className="relative w-[280px] md:w-[420px]" style={{ aspectRatio: "600 / 371" }} data-testid="preloader-logo">
            {/* line-art agave that draws itself in */}
            <svg viewBox={AGAVE_VIEWBOX} className="absolute inset-0 w-full h-full" style={{ filter: "drop-shadow(0 6px 18px rgba(20,10,45,0.35))" }}>
              <g transform={AGAVE_TRANSFORM} fill="none" stroke="#141127" strokeWidth={50}
                 strokeLinecap="round" strokeLinejoin="round">
                {AGAVE_PATHS.map((d, i) => (
                  <motion.path
                    key={i}
                    d={d}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 1 }}
                    transition={{
                      pathLength: { duration: DRAW, ease: "easeInOut", delay: i * 0.28 },
                      opacity: { duration: 0.25, delay: i * 0.28 },
                    }}
                  />
                ))}
              </g>
            </svg>
            {/* colour fills the drawn shape */}
            <motion.img
              src="/brand/arte.png"
              alt="Blue Tequila"
              className="absolute inset-0 w-full h-full object-contain"
              style={{ filter: "drop-shadow(0 14px 34px rgba(20,10,40,0.4))" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2.2, ease: EASE, delay: FILL_DELAY }}
            />
          </div>

          <motion.p
            className="mt-8 font-display font-black tracking-tight text-white text-3xl md:text-4xl"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: FILL_DELAY + 1 }}
          >
            Blue Tequila
          </motion.p>
          <motion.p
            className="mt-1.5 font-mono text-[10px] md:text-[11px] tracking-[0.34em] uppercase text-white/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: FILL_DELAY + 1.25 }}
          >
            Mexican Restaurant · Grill &amp; Cantina
          </motion.p>

          <div className="mt-7 h-[3px] w-44 rounded-full bg-white/25 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-white/90"
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: TOTAL / 1000 - 0.6, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
